interface FieldHomeProps {
  projetoAtivo: {
    arvoresHoje: number;
    vistoriasHoje: number;
    fotosHoje: number;
    totalRegistrosLocais: number;
    pendentesSincronizacao: number;
    fotosPendentes: number;
    ultimaSincronizacao: string;
    precisaoGps: number;
  };
  onNavigate: (href: string) => void;
}

const acoesRapidas = [
  { icon: "ri-add-circle-line", label: "Nova árvore", href: "/coletor/nova-arvore", bg: "bg-[#1B6E4F]" },
  { icon: "ri-clipboard-line", label: "Nova vistoria", href: "/coletor/vistoria", bg: "bg-[#0052CC]" },
  { icon: "ri-camera-line", label: "Adicionar fotos", href: "/coletor/fotos", bg: "bg-[#3F4A52]" },
  { icon: "ri-map-pin-line", label: "Abrir mapa", href: "/coletor/mapa", bg: "bg-[#0052CC]" },
  { icon: "ri-draft-line", label: "Ver rascunhos", href: "/coletor/rascunhos", bg: "bg-[#F57C00]" },
  { icon: "ri-cloud-line", label: "Sincronizar", href: "/coletor/sincronizacao", bg: "bg-[#1B6E4F]" },
];

export default function FieldHome({ projetoAtivo, onNavigate }: FieldHomeProps) {
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) +
      " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <main className="px-4 pb-20 pt-4 max-w-lg mx-auto">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-xl font-heading font-bold text-[#3F4A52]">Coletor de Campo</h2>
        <p className="text-sm text-[#3F4A52]/70 mt-1">
          Cadastre árvores, vistorias, fotos e evidências com GPS, mesmo em campo.
        </p>
      </div>

      {/* Ações rápidas */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {acoesRapidas.map((acao) => (
          <button
            key={acao.href}
            onClick={() => onNavigate(acao.href)}
            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white border border-[#E8EAED] hover:shadow-md transition-all active:scale-95"
          >
            <div className={`w-11 h-11 rounded-xl ${acao.bg} flex items-center justify-center`}>
              <i className={`${acao.icon} text-white text-xl`} />
            </div>
            <span className="text-xs font-medium text-[#3F4A52] text-center leading-tight">{acao.label}</span>
          </button>
        ))}
      </div>

      {/* Indicadores do dia */}
      <div className="mb-5">
        <h3 className="text-sm font-heading font-bold text-[#3F4A52] mb-3">Hoje no campo</h3>
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-white border border-[#E8EAED] rounded-xl p-3 text-center">
            <p className="text-lg font-heading font-bold text-[#1B6E4F]">{projetoAtivo.arvoresHoje}</p>
            <p className="text-[10px] text-[#3F4A52]/60 leading-tight mt-0.5">Árvores</p>
          </div>
          <div className="bg-white border border-[#E8EAED] rounded-xl p-3 text-center">
            <p className="text-lg font-heading font-bold text-[#0052CC]">{projetoAtivo.vistoriasHoje}</p>
            <p className="text-[10px] text-[#3F4A52]/60 leading-tight mt-0.5">Vistorias</p>
          </div>
          <div className="bg-white border border-[#E8EAED] rounded-xl p-3 text-center">
            <p className="text-lg font-heading font-bold text-[#3F4A52]">{projetoAtivo.fotosHoje}</p>
            <p className="text-[10px] text-[#3F4A52]/60 leading-tight mt-0.5">Fotos</p>
          </div>
          <div className="bg-white border border-[#E8EAED] rounded-xl p-3 text-center">
            <p className="text-lg font-heading font-bold text-[#F57C00]">{projetoAtivo.pendentesSincronizacao}</p>
            <p className="text-[10px] text-[#3F4A52]/60 leading-tight mt-0.5">Pendentes</p>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="mb-5">
        <h3 className="text-sm font-heading font-bold text-[#3F4A52] mb-3">Status</h3>
        <div className="bg-white border border-[#E8EAED] rounded-xl divide-y divide-[#E8EAED]">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-[#3F4A52]">Registros locais</span>
            <span className="text-sm font-heading font-bold text-[#1B6E4F]">{projetoAtivo.totalRegistrosLocais}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-[#3F4A52]">Pendentes de sincronização</span>
            <span className="text-sm font-heading font-bold text-[#F57C00]">{projetoAtivo.pendentesSincronizacao}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-[#3F4A52]">Fotos pendentes</span>
            <span className="text-sm font-heading font-bold text-[#F57C00]">{projetoAtivo.fotosPendentes}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-[#3F4A52]">Última sincronização</span>
            <span className="text-xs text-[#3F4A52]/70">{formatDate(projetoAtivo.ultimaSincronizacao)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-[#3F4A52]">Precisão GPS</span>
            <span className="text-sm font-heading font-bold text-[#1B6E4F]">± {projetoAtivo.precisaoGps} m</span>
          </div>
        </div>
      </div>

      {/* Pendências de validação */}
      <div>
        <h3 className="text-sm font-heading font-bold text-[#3F4A52] mb-3">Pendências de validação</h3>
        <div className="space-y-2">
          {[
            { icon: "ri-error-warning-line", text: "4 árvores sem foto principal", color: "text-[#F57C00]", bg: "bg-[#FFF3E0]" },
            { icon: "ri-map-pin-line", text: "2 árvores sem coordenada GPS", color: "text-[#D32F2F]", bg: "bg-[#FFEBEE]" },
            { icon: "ri-leaf-line", text: "3 árvores sem espécie identificada", color: "text-[#FBC02D]", bg: "bg-[#FFFDE7]" },
            { icon: "ri-clipboard-line", text: "5 vistorias aguardando revisão", color: "text-[#0052CC]", bg: "bg-[#64B5F6]/15" },
          ].map((item, i) => (
            <div key={i} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl ${item.bg}`}>
              <i className={`${item.icon} ${item.color} text-base`} />
              <span className="text-xs font-medium text-[#3F4A52]">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}